import React from 'react';
import axios from 'axios';
import { Formik, getIn, useFormikContext } from 'formik';
import { snakeCase } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import CheckboxFormik from '~/components/CheckboxFormik';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Heading from '~/components/core/TextComponents/Heading';
import { TEXTS } from '~/components/SystemConfiguration/DelegatedAuthority/texts';
import { NumericTextFieldFormik, TextFieldFormik, useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';
import type { AuthorityLevelInfo } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

interface FormValues {
  name: string;
  key: string;
  indemnity_payments_limit: number;
  expenses_payments_limit: number;
  indemnity_reserves_limit: number;
  expenses_reserves_limit: number;
  payments_limit: number;
  reserves_limit: number;
  is_payments_full_authority: boolean;
  is_reserves_full_authority: boolean;
}

interface UpsertAuthorityLevelDialogProps {
  organizationId: number;
  arePaymentLimitsSplit: boolean;
  existingLevel: AuthorityLevelInfo | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

const limitFieldValidator = Yup.number()
  .required(TEXTS.VALIDATION_REQUIRED)
  .integer(TEXTS.VALIDATION_LIMIT_NUMBER)
  .min(-1, TEXTS.VALIDATION_LIMIT_MIN);

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required(TEXTS.VALIDATION_NAME_REQUIRED),
  key: Yup.string()
    .trim()
    .matches(/^[a-z][a-z0-9_]*$/, TEXTS.VALIDATION_KEY_FORMAT)
    .required(TEXTS.VALIDATION_KEY_REQUIRED),
  indemnity_payments_limit: limitFieldValidator,
  expenses_payments_limit: limitFieldValidator,
  indemnity_reserves_limit: limitFieldValidator,
  expenses_reserves_limit: limitFieldValidator,
  payments_limit: limitFieldValidator,
  reserves_limit: limitFieldValidator,
});

const buildInitialValues = (existingLevel: AuthorityLevelInfo | null, arePaymentLimitsSplit: boolean): FormValues => {
  if (!existingLevel) {
    return {
      name: '',
      key: '',
      indemnity_payments_limit: 0,
      expenses_payments_limit: 0,
      indemnity_reserves_limit: 0,
      expenses_reserves_limit: 0,
      payments_limit: 0,
      reserves_limit: 0,
      is_payments_full_authority: false,
      is_reserves_full_authority: false,
    };
  }
  const l = existingLevel.limits;
  const paymentsFull = arePaymentLimitsSplit
    ? l.indemnity_payments_limit === '-1' && l.expenses_payments_limit === '-1'
    : l.payments_limit === '-1';
  const reservesFull = arePaymentLimitsSplit
    ? l.indemnity_reserves_limit === '-1' && l.expenses_reserves_limit === '-1'
    : l.reserves_limit === '-1';
  return {
    name: existingLevel.name,
    key: existingLevel.key,
    indemnity_payments_limit: Number(l.indemnity_payments_limit),
    expenses_payments_limit: Number(l.expenses_payments_limit),
    indemnity_reserves_limit: Number(l.indemnity_reserves_limit),
    expenses_reserves_limit: Number(l.expenses_reserves_limit),
    payments_limit: Number(l.payments_limit),
    reserves_limit: Number(l.reserves_limit),
    is_payments_full_authority: paymentsFull,
    is_reserves_full_authority: reservesFull,
  };
};

interface LimitsSectionProps {
  title: string;
  fullAuthorityFieldId: string;
  limitFieldIds: string[];
  children: React.ReactNode;
}

const LimitsSection: React.FC<LimitsSectionProps> = ({ title, fullAuthorityFieldId, limitFieldIds, children }) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const isFullAuthority = !!getIn(values, fullAuthorityFieldId);

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setFieldValue(fullAuthorityFieldId, checked);
    limitFieldIds.forEach((id) => setFieldValue(id, checked ? -1 : 0));
  };

  return (
    <div className="flex flex-col gap-12 rounded-md bg-slate-200 p-20">
      <div className="flex items-center justify-between">
        <Heading variant={Heading.TYPES.H4}>{title}</Heading>
        <CheckboxFormik id={fullAuthorityFieldId} label={TEXTS.FULL_AUTHORITY} onChange={handleToggle} />
      </div>
      {!isFullAuthority && children}
    </div>
  );
};

const DialogInner: React.FC<{
  onClose: () => void;
  isNew: boolean;
  arePaymentLimitsSplit: boolean;
}> = ({ onClose, isNew, arePaymentLimitsSplit }) => {
  const { handleSubmit, isSubmitting, values } = useFormikContext<FormValues>();

  useSetDefaultFieldsOnChange(values.name, { key: snakeCase(values.name) }, undefined, !isNew, true);

  return (
    <CardDialog
      title={isNew ? TEXTS.CREATE_LEVEL : TEXTS.EDIT_LEVEL}
      isDialog
      open={true}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSubmit}
          onClickSecondary={onClose}
          primaryLabel={TEXTS.SAVE}
          showPrimary
          secondaryLabel={TEXTS.CANCEL}
          disabled={isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-20">
        <TextFieldFormik label={TEXTS.FIELD_NAME} id="name" className="w-full" />
        <TextFieldFormik
          label={TEXTS.FIELD_KEY}
          id="key"
          className="w-full"
          disabled={!isNew}
          helperText={TEXTS.FIELD_KEY_HELP}
        />
        <LimitsSection
          title={TEXTS.SECTION_PAYMENTS_LIMITS}
          fullAuthorityFieldId="is_payments_full_authority"
          limitFieldIds={
            arePaymentLimitsSplit ? ['indemnity_payments_limit', 'expenses_payments_limit'] : ['payments_limit']
          }
        >
          {arePaymentLimitsSplit ? (
            <div className="flex gap-16">
              <NumericTextFieldFormik
                label={TEXTS.FIELD_INDEMNITY}
                id="indemnity_payments_limit"
                className="w-full"
                allowNegative
              />
              <NumericTextFieldFormik
                label={TEXTS.FIELD_EXPENSES}
                id="expenses_payments_limit"
                className="w-full"
                allowNegative
              />
            </div>
          ) : (
            <NumericTextFieldFormik label={TEXTS.FIELD_PAYMENTS} id="payments_limit" className="w-full" allowNegative />
          )}
        </LimitsSection>
        <LimitsSection
          title={TEXTS.SECTION_RESERVES_LIMITS}
          fullAuthorityFieldId="is_reserves_full_authority"
          limitFieldIds={
            arePaymentLimitsSplit ? ['indemnity_reserves_limit', 'expenses_reserves_limit'] : ['reserves_limit']
          }
        >
          {arePaymentLimitsSplit ? (
            <div className="flex gap-16">
              <NumericTextFieldFormik
                label={TEXTS.FIELD_INDEMNITY}
                id="indemnity_reserves_limit"
                className="w-full"
                allowNegative
              />
              <NumericTextFieldFormik
                label={TEXTS.FIELD_EXPENSES}
                id="expenses_reserves_limit"
                className="w-full"
                allowNegative
              />
            </div>
          ) : (
            <NumericTextFieldFormik label={TEXTS.FIELD_RESERVES} id="reserves_limit" className="w-full" allowNegative />
          )}
        </LimitsSection>
      </div>
    </CardDialog>
  );
};

const UpsertAuthorityLevelDialog: React.FC<UpsertAuthorityLevelDialogProps> = ({
  organizationId,
  arePaymentLimitsSplit,
  existingLevel,
  onClose,
  onSaved,
}) => {
  const isNew = !existingLevel;
  const baseUrl = `/sysconfig/api/v1/organizations/${organizationId}/delegated_authority/levels`;

  const handleSubmit = async (values: FormValues) => {
    // API contract is `string` for limit fields.
    const limits = {
      indemnity_payments_limit: String(values.indemnity_payments_limit),
      expenses_payments_limit: String(values.expenses_payments_limit),
      indemnity_reserves_limit: String(values.indemnity_reserves_limit),
      expenses_reserves_limit: String(values.expenses_reserves_limit),
      payments_limit: String(values.payments_limit),
      reserves_limit: String(values.reserves_limit),
    };
    try {
      if (isNew) {
        await axios.post(baseUrl, { name: values.name.trim(), key: values.key.trim(), limits });
      } else {
        await axios.patch(`${baseUrl}/${existingLevel.id}`, {
          name: values.name.trim(),
          limits,
        });
      }
    } catch (error) {
      await reportAxiosError(error);
      return;
    }
    onClose();
    try {
      await onSaved();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  return (
    <Formik<FormValues>
      initialValues={buildInitialValues(existingLevel, arePaymentLimitsSplit)}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      <DialogInner onClose={onClose} isNew={isNew} arePaymentLimitsSplit={arePaymentLimitsSplit} />
    </Formik>
  );
};

export default UpsertAuthorityLevelDialog;
